'use client';
import { getVariantFromSimpleControls } from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { isVideoUrl } from '@/utils/isVideoUrl';
import makeId from '@/utils/makeId';
import { normalizeSvgEmbedMarkup } from '@/utils/normalizeSvgEmbed';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import IconValue from './atoms/IconValue';
import { MarkDown } from './atoms/MarkDown';
import VideoEmbed from './atoms/VideoEmbed';

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'slide',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'slide';
}

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

function normalizeMediaUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object') {
        const candidate =
            input.url ||
            input.src ||
            input.Media ||
            input.media ||
            input.value ||
            '';
        return typeof candidate === 'string' ? candidate.trim() : '';
    }
    return '';
}

const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );

export default function SecondaryServices({
    MainHeader,
    Tagline,
    Services,
    CTA,
    CustomCTALink,
    CTAOpenInNewTab,
    CTAIcon,
    CTAIconVisible,
    SecondaryButtonText,
    OutlineButtonText,
    SecondaryButtonLink,
    SecondaryButtonOpenInNewTab,
    SecondaryButtonIcon,
    SecondaryButtonIconVisible,
    OutlineButtonLink,
    OutlineButtonOpenInNewTab,
    OutlineButtonIcon,
    OutlineButtonIconVisible,
    ColorScheme = 'white',
    GradientDirection = 180,
    Alignment = 'left',
    VerticalSpacing = 'medium',
    ImageVariant = 'cover',
    Animation = 'fade',
    Duration = 0.6,
    AnimationGroup = 'content',
    EditorPreview = false,
}) {
    const serviceHeadingVariant = getChildHeadingVariant(MainHeader);
    const {
        backgroundColor,
        textColor,
        headerColor,
        iconColor,
        secondaryButton,
        thirdButton,
        taglineColor,
        backgroundStyle,
    } = getColorScheme(ColorScheme, GradientDirection);

    const preset = normalizeAnimation(Animation);
    const durationVal = Number(Duration) || 0.6;

    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = (e) => setPrefersReduced(e.matches);
        setPrefersReduced(mq.matches);
        mq.addEventListener?.('change', update);
        return () => mq.removeEventListener?.('change', update);
    }, []);

    const isStagger = preset === 'stagger';
    const viewport = { once: !EditorPreview, amount: 0.5 };

    const rawGroup = String(AnimationGroup || 'content').toLowerCase();
    const allowedGroups = new Set([
        'features',
        'feature',
        'content',
        'ctas',
        'medias',
        'media',
    ]);
    const group = allowedGroups.has(rawGroup)
        ? rawGroup === 'feature'
            ? 'features'
            : rawGroup === 'media'
            ? 'medias'
            : rawGroup
        : 'content';

    const animateHeading = group === 'content';
    const animateFeatures = group === 'features';
    const animateCTAs = group === 'ctas';
    const animateMedia = group === 'medias';

    const enableAnim = !prefersReduced && preset !== 'none';
    const baseKey = `secondary-services-${group}-${preset}-${durationVal}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const containerMotionProps = useMemo(() => {
        if (prefersReduced) return {};
        if (isStagger) {
            const variants = getVariantFromSimpleControls(
                'stagger',
                durationVal
            );
            if (EditorPreview)
                return { variants, initial: 'hidden', animate: 'show' };
            return {
                variants,
                initial: 'hidden',
                whileInView: 'show',
                viewport,
            };
        }
        return {};
    }, [prefersReduced, isStagger, durationVal, EditorPreview]);

    const childMotionProps = useMemo(() => {
        if (prefersReduced) return {};
        if (isStagger) {
            const childVariants = {
                hidden: { opacity: 0, y: 16 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: Math.max(0.4, durationVal - 0.2) },
                },
            };
            if (EditorPreview)
                return {
                    variants: childVariants,
                    initial: 'hidden',
                    animate: 'show',
                };
            return {
                variants: childVariants,
                initial: 'hidden',
                whileInView: 'show',
                viewport,
            };
        }
        if (preset === 'none') return {};
        const v = getVariantFromSimpleControls(preset, durationVal);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        if (EditorPreview)
            return {
                variants: v,
                initial: initialState,
                animate: targetState,
            };
        return {
            variants: v,
            initial: initialState,
            whileInView: targetState,
            viewport,
        };
    }, [prefersReduced, isStagger, preset, durationVal, EditorPreview]);
    const staggerChildVariants = isStagger ? childMotionProps?.variants : null;

    const textAlign = Alignment === 'center' ? 'text-center' : 'text-left';
    const buttonAlign =
        Alignment === 'center' ? 'justify-center' : 'justify-start';
    const vSpace = getVerticalSpacing(VerticalSpacing);

    const isMasonryMode = false;

    const shouldCenterSingleCard =
        Services.length === 1 && Alignment === 'center';

    let layoutClasses = '';

    if (isMasonryMode) {
        let columnsClass = 'columns-1';
        if (Services.length === 2) {
            columnsClass = 'columns-1 lg:columns-2';
        } else if (Services.length === 4) {
            columnsClass = 'columns-1 sm:columns-2 lg:columns-4';
        } else if (Services.length >= 3) {
            columnsClass = 'columns-1 sm:columns-2 lg:columns-3';
        }
        layoutClasses = `block ${columnsClass} gap-x-8`;
    } else {
        const numGridCols = getNumColumns(Services.length);
        layoutClasses = `grid gap-x-8 gap-y-16 sm:gap-y-20 ${numGridCols} ${
            shouldCenterSingleCard ? 'lg:place-items-center' : ''
        }`;
    }
    const serviceWrapperClasses = isMasonryMode
        ? 'break-inside-avoid-column mb-16 inline-block w-full'
        : 'h-full w-full';

    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            CTA,
            CustomCTALink,
            CTAOpenInNewTab,
            CTAIcon,
            CTAIconVisible,
            SecondaryButtonText,
            SecondaryButtonLink,
            SecondaryButtonOpenInNewTab,
            SecondaryButtonIcon,
            SecondaryButtonIconVisible,
            OutlineButtonText,
            OutlineButtonLink,
            OutlineButtonOpenInNewTab,
            OutlineButtonIcon,
            OutlineButtonIconVisible,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign,
            wrapSpacing: 'mt-10 lg:mt-16',
            size: 'md',
            enableAnim: animateCTAs && enableAnim,
            motionProps: childMotionProps,
        }
    );

    return (
        <div data-json="secondaryservices">
            <div
                className={`editable-component relative overflow-hidden px-6 ${vSpace} ${textAlign} ${backgroundColor}`}
                style={backgroundStyle}
            >
                <div className="relative z-40">
                    {(Tagline || MainHeader) && (
                        <MotionMaybe
                            enable={animateHeading && enableAnim}
                            k={`${baseKey}-heading`}
                            motionProps={childMotionProps}
                            className="mx-auto mb-6 max-w-5xl md:mb-10 lg:max-w-7xl"
                        >
                            {Tagline && (
                                <MarkDown
                                    components={{
                                        p: ({
                                            children,
                                            className,
                                            ...rest
                                        }) => (
                                            <p
                                                {...rest}
                                                className={[
                                                    'text-base font-semibold uppercase leading-7 tracking-wide',
                                                    taglineColor,
                                                    className,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            >
                                                {children}
                                            </p>
                                        ),
                                    }}
                                >
                                    {Tagline}
                                </MarkDown>
                            )}
                            {MainHeader && (
                                <MarkDown
                                    variant="h2"
                                    className={`mt-2 font-display text-3xl tracking-tight sm:text-4xl sm:leading-tight ${textColor}`}
                                >
                                    {MainHeader}
                                </MarkDown>
                            )}
                        </MotionMaybe>
                    )}

                    <div className="mx-auto max-w-7xl">
                        <MotionMaybe
                            enable={animateFeatures && enableAnim}
                            k={`${baseKey}-services`}
                            motionProps={containerMotionProps}
                            className={layoutClasses}
                        >
                            {Services.map((service, index) =>
                                isStagger && animateFeatures && enableAnim ? (
                                    <motion.div
                                        key={index}
                                        variants={staggerChildVariants}
                                        className={serviceWrapperClasses}
                                    >
                                        <SecondaryService
                                            {...service}
                                            enableAnim={enableAnim}
                                            animateMedia={animateMedia}
                                            motionProps={childMotionProps}
                                            textColor={textColor}
                                            headerColor={headerColor}
                                            iconColor={iconColor}
                                            taglineColor={taglineColor}
                                            ImageVariant={ImageVariant}
                                            Alignment={Alignment}
                                            isMasonryItem={isMasonryMode}
                                            secondaryButton={secondaryButton}
                                            thirdButton={thirdButton}
                                            headingVariant={
                                                serviceHeadingVariant
                                            }
                                        />
                                    </motion.div>
                                ) : (
                                    <MotionMaybe
                                        key={index}
                                        enable={animateFeatures && enableAnim}
                                        k={`${baseKey}-service-${index}`}
                                        motionProps={childMotionProps}
                                        className={serviceWrapperClasses}
                                    >
                                        <SecondaryService
                                            {...service}
                                            enableAnim={enableAnim}
                                            animateMedia={animateMedia}
                                            motionProps={childMotionProps}
                                            textColor={textColor}
                                            headerColor={headerColor}
                                            iconColor={iconColor}
                                            taglineColor={taglineColor}
                                            ImageVariant={ImageVariant}
                                            Alignment={Alignment}
                                            isMasonryItem={isMasonryMode}
                                            secondaryButton={secondaryButton}
                                            thirdButton={thirdButton}
                                            headingVariant={
                                                serviceHeadingVariant
                                            }
                                        />
                                    </MotionMaybe>
                                )
                            )}
                        </MotionMaybe>

                        {hasCTAs && CTAs}
                    </div>
                </div>
            </div>
        </div>
    );
}

function SecondaryService({
    Service,
    Anchor,
    Icon,
    Header,
    Body,
    ImageUrl,
    ImagePosition = '50',
    ImageVariant = 'cover',
    Video,
    VideoTitle,
    AltText,
    textColor,
    headerColor,
    iconColor,
    taglineColor,
    Link,
    Alignment = 'left',
    isMasonryItem = false,
    SmallCTA,
    SmallCTALink,
    SmallCTAOpenInNewTab,
    SmallCTAIcon,
    SmallCTAIconVisible,
    SmallSecondaryButtonText,
    SmallSecondaryButtonLink,
    SmallSecondaryButtonOpenInNewTab,
    SmallSecondaryButtonIcon,
    SmallSecondaryButtonIconVisible,
    SmallOutlineButtonText,
    SmallOutlineButtonLink,
    SmallOutlineButtonOpenInNewTab,
    SmallOutlineButtonIcon,
    SmallOutlineButtonIconVisible,
    secondaryButton,
    thirdButton,
    headingVariant,
    enableAnim = false,
    animateMedia = false,
    motionProps = {},
}) {
    const normalizedImageUrl = normalizeMediaUrl(ImageUrl);
    const normalizedVideoUrl = normalizeMediaUrl(Video);
    const imageIsEmbed = isEmbedCode(normalizedImageUrl);
    const imageVideoUrl = isVideoUrl(normalizedImageUrl)
        ? normalizedImageUrl
        : '';

    // ImageUrl is the media field exposed by the SecondaryServices editor. Some
    // older content also has a hidden Video value, so prefer video/embed media
    // selected in ImageUrl instead of letting that legacy value override every
    // service card.
    const hasEditableVideoMedia = Boolean(imageVideoUrl || imageIsEmbed);
    const videoIsEmbed =
        !hasEditableVideoMedia && isEmbedCode(normalizedVideoUrl);
    const videoUrl = imageVideoUrl
        ? imageVideoUrl
        : videoIsEmbed
        ? ''
        : normalizedVideoUrl;
    const embedHtml = imageIsEmbed
        ? normalizedImageUrl
        : videoIsEmbed
        ? normalizedVideoUrl
        : '';
    const imageUrl = videoUrl || embedHtml ? '' : normalizedImageUrl;

    const overlayClass =
        'hover:opacity-70 transition-opacity duration-300 ease-in-out';

    const buttonAlign =
        Alignment === 'center' ? 'justify-center' : 'justify-start';

    const isContainVariantForThisService = ImageVariant.includes('contain');
    const isSquare = ImageVariant.includes('square');
    const isPortrait = ImageVariant.includes('portrait');
    const hasShadow = !ImageVariant.includes('no-shadow');

    const shape = isSquare
        ? 'rounded-[var(--shape-radius)]'
        : 'rounded-[min(1.5rem,var(--shape-radius))]';
    const shadow = hasShadow ? 'shadow-xl' : '';

    const useIntrinsicAspectRatioImage =
        imageUrl &&
        !videoUrl &&
        isContainVariantForThisService &&
        !isSquare &&
        !isPortrait;
    const { x: imagePosX, y: imagePosY } = parseImagePosition(ImagePosition);
    const imageObjectPosition = `${imagePosX}% ${imagePosY}%`;
    const normalizedEmbedHtml = embedHtml
        ? normalizeSvgEmbedMarkup(embedHtml, {
              x: imagePosX,
              y: imagePosY,
              mode: isContainVariantForThisService ? 'contain' : 'cover',
          })
        : '';

    let mediaContainerStyles = {};
    let imageElementClasses = '';

    const mediaContainerBaseClasses = `w-full overflow-hidden ${shape} ${shadow} ${
        Link ? overlayClass : ''
    }`;
    let mediaContainerDynamicClasses = isPortrait
        ? 'secondary-services-portrait-media relative'
        : 'relative';

    if (isSquare) {
        mediaContainerStyles = { paddingTop: '100%' };
        if (imageUrl) {
            imageElementClasses = `${
                isContainVariantForThisService
                    ? 'object-contain'
                    : 'object-cover'
            } absolute left-0 top-0 h-full w-full`;
        }
    } else if (isPortrait) {
        mediaContainerStyles = { aspectRatio: '9 / 16' };
        if (imageUrl) {
            imageElementClasses = `${
                isContainVariantForThisService
                    ? 'object-contain'
                    : 'object-cover'
            } absolute left-0 top-0 h-full w-full`;
        }
    } else if (useIntrinsicAspectRatioImage) {
        mediaContainerStyles = {};
        imageElementClasses = `object-contain w-full h-auto`;
    } else {
        mediaContainerStyles = { paddingTop: '56.25%' };
        if (imageUrl) {
            imageElementClasses = `${
                isContainVariantForThisService
                    ? 'object-contain'
                    : 'object-cover'
            } absolute left-0 top-0 h-full w-full`;
        }
    }

    const finalMediaContainerClasses = `${mediaContainerBaseClasses} ${mediaContainerDynamicClasses}`;

    const rawMediaContent = (videoUrl || imageUrl || embedHtml) && (
        <div
            className={finalMediaContainerClasses}
            style={mediaContainerStyles}
        >
            {videoUrl ? (
                <VideoEmbed
                    url={videoUrl}
                    controls
                    width="100%"
                    height="100%"
                    className="absolute left-0 top-0 h-full w-full"
                    title={VideoTitle || 'Video'}
                />
            ) : embedHtml ? (
                <div
                    className="absolute inset-0 [&>*]:h-full [&>*]:max-h-none [&>*]:w-full [&>*]:max-w-none [&>svg]:block"
                    dangerouslySetInnerHTML={{
                        __html: normalizedEmbedHtml,
                    }}
                />
            ) : (
                imageUrl && (
                    <img
                        src={imageUrl}
                        alt={AltText}
                        loading="lazy"
                        className={imageElementClasses}
                        style={{ objectPosition: imageObjectPosition }}
                    />
                )
            )}
        </div>
    );
    const mediaContent =
        animateMedia && enableAnim ? (
            <MotionMaybe
                enable
                k={`${Anchor || Service || 'media'}`}
                motionProps={motionProps}
            >
                {rawMediaContent}
            </MotionMaybe>
        ) : (
            rawMediaContent
        );

    const { hasAny: hasSmallCTAs, element: smallCTAs } = getCTA(
        {
            CTA: SmallCTA,
            CustomCTALink: SmallCTALink,
            CTAOpenInNewTab: SmallCTAOpenInNewTab,
            CTAIcon: SmallCTAIcon,
            CTAIconVisible: SmallCTAIconVisible,
            SecondaryButtonText: SmallSecondaryButtonText,
            SecondaryButtonLink: SmallSecondaryButtonLink,
            SecondaryButtonOpenInNewTab: SmallSecondaryButtonOpenInNewTab,
            SecondaryButtonIcon: SmallSecondaryButtonIcon,
            SecondaryButtonIconVisible: SmallSecondaryButtonIconVisible,
            OutlineButtonText: SmallOutlineButtonText,
            OutlineButtonLink: SmallOutlineButtonLink,
            OutlineButtonOpenInNewTab: SmallOutlineButtonOpenInNewTab,
            OutlineButtonIcon: SmallOutlineButtonIcon,
            OutlineButtonIconVisible: SmallOutlineButtonIconVisible,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign,
            wrapSpacing: 'mt-auto pt-8',
            buttonsClassName: 'flex flex-wrap items-start gap-2',
            size: 'sm',
        }
    );

    const serviceContent = (
        <div
            className={`relative flex h-full w-full flex-col ${
                Alignment === 'center' ? 'mx-auto' : 'lg:mx-0'
            } max-w-2xl`}
        >
            <div id={makeId(Anchor)} className="absolute -mt-24 lg:-mt-32" />

            {mediaContent}

            {!imageUrl && !videoUrl && Icon && (
                <div
                    className={
                        Alignment === 'center' ? 'flex justify-center' : ''
                    }
                >
                    <IconValue
                        value={Icon}
                        className={`text-5xl ${iconColor}`}
                    />
                </div>
            )}

            {Service && (
                <p
                    className={`-mb-6 mt-8 font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                >
                    {Service}
                </p>
            )}
            {Header && (
                <MarkDown
                    variant={headingVariant}
                    className={`-mb-4 mt-8 font-display text-xl tracking-tight ${headerColor}`}
                >
                    {Header}
                </MarkDown>
            )}
            {Body && (
                <MarkDown
                    className={`mt-8 text-sm leading-6 sm:leading-7 md:text-base lg:text-sm xl:text-base ${textColor}`}
                >
                    {Body}
                </MarkDown>
            )}

            {hasSmallCTAs && smallCTAs}
        </div>
    );

    const wrapperClasses = isMasonryItem
        ? 'break-inside-avoid-column mb-16 inline-block w-full'
        : 'w-full';

    return Link && !hasSmallCTAs ? (
        <a href={Link} className={`text-decoration-none ${wrapperClasses}`}>
            {serviceContent}
        </a>
    ) : (
        <div className={wrapperClasses}>{serviceContent}</div>
    );
}

function getNumColumns(serviceCount) {
    if (serviceCount === 1) return 'grid-cols-1';
    if (serviceCount === 2) return 'grid-cols-1 lg:grid-cols-2';
    if (serviceCount === 4) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
    if (serviceCount === 8) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
    if (serviceCount >= 9) return 'grid-cols-1 sm:grid-cols-3 lg:grid-cols-3';
    if (serviceCount >= 3) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    return 'grid-cols-1 lg:grid-cols-3';
}
